// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { get } from "@insite/client-framework/Services/ApiService";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { ProductListProductQuery } from "@werner/Overrides/Widgets/ProductList/ProductListProductCount";
import { DivWrapper, SharedProductPagesContexts } from "@werner/XDL/constants";
import { color, hideElement, xScreen } from "@werner/XDL/Utilities";
import { useEffect } from "react";
import React, { FC, useState } from "react";
import { css } from "styled-components";


export interface ProductListLayoutStyles {
    container?: InjectableCss;
    productListContainer?: InjectableCss;
    headerWrapper?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    mainSectionActionsContainer?: InjectableCss;
    filterMobileSectionContainer?: InjectableCss;
    sortFilterButton?: ButtonPresentationProps;
    sortAndPaginationWrapper?: InjectableCss;
    bottomPagination?: InjectableCss;
    modal?: ModalPresentationProps;
}

export const layoutStyles: ProductListLayoutStyles = {
    productListContainer: {
        css: css`
            display: flex;
            gap: 54px;
        `,
    },
    headerWrapper: {
        css: css`
            margin-bottom: 44px;
            &:has(> div:empty) {
                display: none;
            }
        `,
    },
    leftColumnContainer: {
        css: css`
            flex: 0 0 217px;
            ${xScreen("lte", "md")} {
                &.hide-on-mobile {
                    ${hideElement}
                }
                flex: 0 0 100%;
            }

            & > ${DivWrapper} {
                display: flex;
                flex-direction: column;
                gap: 60px;
            }
        `,
    },
    rightColumnContainer: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        `,
    },
    mainSectionActionsContainer: {
        css: css`
            display: flex;
            gap: 20px;
            justify-content: space-between;
            padding-top: 12px;

            ${({ isShellEditing }: any) =>
                isShellEditing &&
                css`
                    flex-wrap: wrap;
                `}
        `,
    },
    sortAndPaginationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 70px;
            justify-content: flex-end;

            ${xScreen("lte", "md")} {
                &.hide-on-mobile {
                    ${hideElement}
                }

                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 20px;
            }
        `,
    },
    filterMobileSectionContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            gap: 30px;
            margin-left: auto;
            ${xScreen("gt", "lg")} {
                ${hideElement}
            }
        `,
    },

};

const styles = layoutStyles;

const EventListLayout: FC<WidgetProps> = ({ id }) => {
    const { isEditing } = useShellContext();


    return (
        <>
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.headerWrapper} data-test-selector="header-EventList">
                <XngZone contentId={id} zoneName="ContentMainHeader00" hint="Header" normalWidth />
            </StyledWrapper>
            <StyledWrapper {...styles.productListContainer}>
                <StyledWrapper {...styles.leftColumnContainer} className="hide-on-mobile">
                    <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumnContainer}>
                    <StyledWrapper {...styles.mainSectionActionsContainer} {...{ isShellEditing: isEditing }}>
                    </StyledWrapper>
                    <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: EventListLayout,
    definition: {
        group: "Training Calender" as any,
        displayName: "Training Calender Layout",
    },
};

export default widgetModule;
